import {
  forwardRef,
  useCallback,
  useState,
  PropsWithChildren,
  SyntheticEvent,
  MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ResizeCallbackData } from 'react-resizable';
import { ClipLoader } from 'react-spinners';
import {
  MarkdownEditorWrapper,
  StyledBottomBar,
  StyledScrollContainer,
} from '../styles';
import { LoadingContainer, ResizeIcon, StyledResizableBox } from '../../styles';
import { Button as KDSButton } from '@kizen/kds/Button';
import Button from '__components/Button';
import {
  INITIAL_HEIGHT,
  MAX_HEIGHT,
  MAX_WIDTH,
  MAX_WIDTH_OFFSET,
  MIN_HEIGHT,
  SCROLL_FADE_LENGTH,
} from '../constants';
import { useWYSIWYGAIContext } from '../../ai-context';

type ContainerProps = {
  onSave?: () => void;
  disabled?: boolean;
  readonly?: boolean;
  isMarkdown?: boolean;
  initialHeight?: number;
  initialWidth?: number;
};

export const MarkdownEditorContainer = forwardRef<
  HTMLDivElement,
  PropsWithChildren<ContainerProps>
>(
  (
    {
      children,
      onSave,
      disabled,
      readonly,
      isMarkdown = true,
      initialHeight,
      initialWidth,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { isLoading: isLoadingAI, cancelRequest } = useWYSIWYGAIContext();

    const [height, setHeight] = useState(initialHeight ?? INITIAL_HEIGHT);
    const [resizing, setResizing] = useState(false);

    const handleStartResize = useCallback(() => {
      setResizing(true);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    }, []);

    const handleStopResize = useCallback(
      (_e: SyntheticEvent, data: ResizeCallbackData) => {
        setResizing(false);
        setHeight(data.size.height);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      },
      []
    );

    return (
      <MarkdownEditorWrapper
        ref={ref}
        toolbarIsHidden={readonly || !isMarkdown}
      >
        <StyledResizableBox
          axis="y"
          resizeHandles={['n', 's']}
          height={initialHeight ?? INITIAL_HEIGHT}
          width={
            initialWidth &&
            Math.min(initialWidth, window.innerWidth - MAX_WIDTH_OFFSET)
          }
          minConstraints={[10, Math.min(INITIAL_HEIGHT, MIN_HEIGHT)]}
          maxConstraints={[
            initialWidth ?? MAX_WIDTH,
            initialHeight ?? MAX_HEIGHT,
          ]}
          handle={
            <ResizeIcon
              icon="column-resize"
              color="var(--color-background-dark)"
            />
          }
          onResizeStart={handleStartResize}
          onResizeStop={handleStopResize}
          resizing={resizing}
        >
          <StyledScrollContainer
            top={{
              fadeLength: SCROLL_FADE_LENGTH,
            }}
            bottom={{
              fadeLength: SCROLL_FADE_LENGTH,
            }}
            useExpandingScrollbar
            isTable={false}
            shadow={false}
            refresh={[height]}
            readonly={readonly}
            isLoadingAI={isLoadingAI}
          >
            {children}
          </StyledScrollContainer>
          {readonly ? null : (
            <StyledBottomBar disabled={disabled || isLoadingAI}>
              <KDSButton
                leftIcon="table-save-cell"
                variant="text"
                color="inherit"
                leftIconSettings={{
                  tooltipDescriptionOverride: t('Save'),
                }}
                showTooltip={!disabled && !isLoadingAI}
                onClick={onSave}
                disabled={disabled || isLoadingAI}
              />
            </StyledBottomBar>
          )}
          {isLoadingAI && (
            <LoadingContainer className="markdown-loading-container">
              <ClipLoader color="var(--color-background-darker)" />
              <Button
                variant="light"
                color="blue"
                onClick={(ev: MouseEvent<HTMLButtonElement>) => {
                  ev.stopPropagation();
                  cancelRequest();
                }}
              >
                {t('Cancel AI Processing')}
              </Button>
            </LoadingContainer>
          )}
        </StyledResizableBox>
      </MarkdownEditorWrapper>
    );
  }
);
